import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

/**
 * Cancel Appointment dialog (dropdown of Brand Settings → Cancellation
 * Reasons; picking a `is_no_show` reason marks the slot "no_show" and
 * keeps it on the grid).
 *
 * When `cancelCtx.only_no_show` is set, the dialog switches into
 * "Mark as No-Show" mode: reasons are filtered to `is_no_show:true`
 * only, the title changes, and the confirm button reads "Mark
 * No-Show". Note field stays available for audit fidelity.
 *
 * Iter-349 — Conditional Note Requirement:
 *   Selecting a reason whose LABEL matches "Other" (case-insensitive,
 *   trimmed) promotes the Note field to mandatory. The Confirm /
 *   Mark No-Show button is disabled until a non-blank note is typed,
 *   the label renders a red asterisk, and an inline error message
 *   surfaces once the user tries to submit an empty note. For every
 *   other reason the Note field stays optional (existing behaviour).
 *
 * `cancelCtx` shape: { appt, reason_id, note, only_no_show? } | null.
 */
export default function CancelAppointmentDialog({ cancelCtx, cancelReasons, setCancelCtx, onConfirm, onClose }) {
  const isNoShowFlow = !!cancelCtx?.only_no_show;
  const reasons = isNoShowFlow
    ? (cancelReasons || []).filter((r) => r.is_no_show)
    : (cancelReasons || []);
  // Iter-349 — resolve whether the currently-picked reason is "Other".
  // Match by trimmed lowercase label so an operator-created reason named
  // "Other", "OTHER", "  other  " etc. all satisfy the rule uniformly
  // across all 8 branches. Reason id alone isn't stable — brand admins
  // can rename the row without changing the id.
  const selectedReason = reasons.find((r) => r.id === cancelCtx?.reason_id) || null;
  const isOtherSelected = !!selectedReason
    && (selectedReason.label || "").trim().toLowerCase() === "other";
  const noteText = (cancelCtx?.note || "").trim();
  const noteRequired = isOtherSelected;
  const noteMissing = noteRequired && noteText.length === 0;
  const confirmDisabled = !cancelCtx?.reason_id || noteMissing;

  const handleConfirm = () => {
    if (noteMissing) return; // belt on top of `disabled` for keyboard submits
    onConfirm();
  };

  return (
    <Dialog open={!!cancelCtx} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid={isNoShowFlow ? "mark-noshow-dialog" : "cancel-appt-dialog"}>
        <DialogHeader>
          <DialogTitle>{isNoShowFlow ? "Mark as No-Show" : "Cancel appointment"}</DialogTitle>
          <DialogDescription>
            {cancelCtx && (
              <>
                <span className="font-medium">{cancelCtx.appt.customer_name || "Walk-in"}</span> — {cancelCtx.appt.service_name}
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>{isNoShowFlow ? "No-Show reason" : "Reason"}</Label>
            <Select
              value={cancelCtx?.reason_id || ""}
              onValueChange={(v) => setCancelCtx((c) => c && { ...c, reason_id: v })}
            >
              <SelectTrigger data-testid="cancel-reason-select">
                <SelectValue placeholder={isNoShowFlow ? "Choose a no-show reason" : "Choose a cancellation reason"} />
              </SelectTrigger>
              <SelectContent>
                {reasons.length === 0 && (
                  <div className="p-3 text-xs text-muted-foreground">
                    {isNoShowFlow
                      ? "No no-show reasons configured. Tick 'Counts as No-Show' on a reason in Brand Settings → Cancellation Reasons."
                      : "No reasons configured. Add them in Brand Settings → Cancellation Reasons."}
                  </div>
                )}
                {reasons.map((r) => (
                  <SelectItem key={r.id} value={r.id} data-testid={`cancel-reason-${r.id}`}>
                    {r.label}{!isNoShowFlow && r.is_no_show ? " · keeps slot on calendar" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {cancelCtx?.reason_id && !isNoShowFlow && (() => {
              const r = reasons.find((x) => x.id === cancelCtx.reason_id);
              if (!r) return null;
              return (
                <p className="text-[11px] text-muted-foreground mt-1" data-testid="cancel-reason-hint">
                  {r.is_no_show
                    ? "Selected reason is a No Show — the appointment will stay on the calendar (marked grey)."
                    : "The appointment will be removed from the calendar."}
                </p>
              );
            })()}
            {isNoShowFlow && (
              <p className="text-[11px] text-muted-foreground mt-1" data-testid="noshow-deposit-hint">
                The tile will turn grey on the calendar. Any deposit stays on the Open invoice — decide later whether to refund it or keep it as a No-Show fee.
              </p>
            )}
          </div>
          <div>
            <Label className="flex items-center gap-1">
              <span>Note{noteRequired ? "" : " (optional)"}</span>
              {noteRequired && (
                <span
                  className="text-rose-600 font-semibold"
                  aria-hidden="true"
                  data-testid="cancel-note-required-asterisk"
                >*</span>
              )}
            </Label>
            <Textarea
              rows={2}
              value={cancelCtx?.note || ""}
              onChange={(e) => setCancelCtx((c) => c && { ...c, note: e.target.value })}
              aria-required={noteRequired}
              aria-invalid={noteMissing}
              className={noteMissing ? "border-rose-500 focus-visible:ring-rose-500" : undefined}
              data-testid="cancel-note-input"
            />
            {noteMissing && (
              <p
                className="text-[11px] text-rose-600 mt-1"
                role="alert"
                data-testid="cancel-note-error"
              >
                Please provide a note for this reason.
              </p>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} data-testid="cancel-dialog-back">Back</Button>
          <Button
            onClick={handleConfirm}
            disabled={confirmDisabled}
            data-testid={isNoShowFlow ? "noshow-dialog-confirm" : "cancel-dialog-confirm"}
          >
            {isNoShowFlow ? "Mark No-Show" : "Confirm"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
